import { useState, useRef, useEffect } from 'react';

import {
  CropContainer,
  CanvasArea,
  CropImg,
  CropMask,
  ConfirmBar,
  ZoomLevel,
  Controls,
  PreviewBox,
  PreviewContainer
} from './crop-image.styles';

const CONTAINER_SIZE = 400;

const CropImage = ({ imageFile, onConfirm }) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [imageSrc, setImageSrc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const defaultRef = useRef({ zoom: 1, offset: { x: 0, y: 0 } });

  useEffect(() => {
    if (!imageFile) return;

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setImageSrc(reader.result);

        const zoomX = CONTAINER_SIZE / img.width;
        const zoomY = CONTAINER_SIZE / img.height;
        const initialZoom = Math.min(zoomX, zoomY);
        const offsetX = (CONTAINER_SIZE - img.width * initialZoom) / 2;
        const offsetY = (CONTAINER_SIZE - img.height * initialZoom) / 2;

        setZoom(initialZoom);
        setOffset({ x: offsetX, y: offsetY });
        defaultRef.current = { zoom: initialZoom, offset: { x: offsetX, y: offsetY } };
      };
      img.src = reader.result;
    };

    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  useEffect(() => {
    generatePreview();
  }, [zoom, offset, imageSrc, rotation]);

  const handleDrag = (e) => {
    const startX = e.clientX;
    const startY = e.clientY;
    const { x, y } = offset;

    const move = (ev) => {
      setOffset({
        x: x + ev.clientX - startX,
        y: y + ev.clientY - startY
      });
    };

    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const handleZoom = (e) => {
    setZoom(prev => Math.max(0.1, Math.min(5, prev + (e.deltaY > 0 ? -0.1 : 0.1))));
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!imageRef.current || !containerRef.current) return;

    const cropData = {
      x: Math.round(-offset.x / zoom),
      y: Math.round(-offset.y / zoom),
      width: Math.round(CONTAINER_SIZE / zoom),
      height: Math.round(CONTAINER_SIZE / zoom),
      zoom,
      rotation
    };

    onConfirm({ crop: cropData, file: imageFile });
  };

  const resetCrop = () => {
    setZoom(defaultRef.current.zoom);
    setOffset(defaultRef.current.offset);
    setRotation(0);
  };

  const rotateLeft = () => {
    setRotation(prev => (prev - 90) % 360);
  };

  const rotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const generatePreview = () => {
    if (!imageSrc) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = CONTAINER_SIZE;
    canvas.width = size;
    canvas.height = size;

    const img = new Image();
    img.onload = () => {
      ctx.save();
      ctx.translate(size / 2, size / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom, zoom);
      ctx.drawImage(img, -offset.x / zoom - size / 2, -offset.y / zoom - size / 2);
      ctx.restore();
      setPreviewUrl(canvas.toDataURL());
    };
    img.src = imageSrc;
  };

  return (
    <CropContainer>
      <CanvasArea ref={containerRef} onWheel={handleZoom}>
        {imageSrc && (
          <CropImg
            ref={imageRef}
            src={imageSrc}
            zoom={zoom}
            offset={offset}
            rotation={rotation}
            onMouseDown={handleDrag}
            draggable={false}
          />
        )}
        <CropMask />
      </CanvasArea>

      <Controls>
        <ZoomLevel>Zoom: {zoom.toFixed(2)}</ZoomLevel>
        <button onClick={rotateLeft}>Rotate ⟲</button>
        <button onClick={rotateRight}>Rotate ⟳</button>
        <button onClick={resetCrop}>Reset</button>
        <button onClick={handleConfirm}>Confirm Crop</button>
      </Controls>

      {previewUrl && (
        <PreviewContainer>
          <h4>Preview</h4>
          <PreviewBox src={previewUrl} alt="Preview" />
        </PreviewContainer>
      )}
    </CropContainer>
  );
};

export default CropImage;